import { Button, createListCollection, Text, VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { CustomSelect } from '@components/ui/select/Select';
import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite, selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { selectNeutralInfo } from '@redux/situation/situation.selectors';
import { usePostMajesCommandMutation } from '@services/c2/c2.api';
import {
  getSectorConfigurations,
  getSectorId,
  getSectorName,
  getSelectedSectorConfiguration,
} from '@utils/sensors/neutral/neutral.utils';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { MajesSetSectorModeCommand, Mc2CommandEnum } from '@/types/c2/c2.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<SensorTypeEnum.MAJES>;
  inMonitoring: boolean;
};

export default function MajesCommands({ sensorOrSubSensorInfo, inMonitoring }: Readonly<Props>) {
  const { configuration, status, type, active } = sensorOrSubSensorInfo;
  const [postMajesCommand] = usePostMajesCommandMutation();
  const activeSite = useAppSelector(selectActiveSite);
  const selectedSectorConfiguration = status
    ? getSelectedSectorConfiguration({
        status,
        configuration,
        type,
        active: true,
      })
    : null;
  const [prevSelectedSectorConfiguration, setPrevSelectedSectorConfiguration] = useState(selectedSectorConfiguration);
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const jammerLogin = useAppSelector((state) => selectNeutralInfo(state, false))?.login ?? null;
  const isNonActiveUserJamming = jammerLogin !== null && activeUserLogin !== jammerLogin;
  const [selectedSector, setSelectedSector] = useState<string | undefined>(
    selectedSectorConfiguration ? getSectorId(selectedSectorConfiguration).toString() : undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (status === null && !inMonitoring) {
    return null;
  }

  const sectorConfigurations = getSectorConfigurations({
    status,
    configuration,
    type,
    active,
  } as MonitoringSensorByType<SensorTypeEnum.MAJES>);

  function handleSubmit() {
    const sector = sectorConfigurations.find((sector) => getSectorId(sector).toString() === selectedSector);
    if (sector && !isNonActiveUserJamming) {
      setIsLoading(true);
      const toastParams = { command: 'sensors.commands.majes.command', name: configuration.name };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postMajesCommand({
        appCode: configuration.appCode,
        site: activeSite?.code ?? '',
        command: {
          type: Mc2CommandEnum.SetSectorMode,
          sensorCode: configuration.code,
          sectorMode: getSectorId(sector),
        } as MajesSetSectorModeCommand,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  }

  if (prevSelectedSectorConfiguration?.sectorId !== selectedSectorConfiguration?.sectorId) {
    setPrevSelectedSectorConfiguration(selectedSectorConfiguration);
    setSelectedSector(selectedSectorConfiguration ? getSectorId(selectedSectorConfiguration).toString() : undefined);
  }

  if (
    isLoading &&
    selectedSectorConfiguration &&
    getSectorId(selectedSectorConfiguration).toString() === selectedSector
  ) {
    setIsLoading(false);
  }

  const items = createListCollection({
    items: sectorConfigurations
      .filter((sector) => sector.active)
      .map((sector) => ({
        label: getSectorName(sector),
        value: getSectorId(sector).toString(),
      })),
  });

  return (
    <PopupContainer height="100%">
      <VStack gap="standardS" width="100%">
        <Text textStyle="md" fontWeight="medium">
          <FormattedMessage id="sensors.tab.commands.majes.changeSelectedSector" />
        </Text>
        <CustomSelect
          variant="darker"
          size="sm"
          key={selectedSector}
          value={selectedSector}
          collection={items}
          disabled={isLoading || isNonActiveUserJamming || !status}
          onChange={(value) => setSelectedSector(value ?? undefined)}
        />
        <Button
          disabled={
            !status ||
            (!!selectedSectorConfiguration && getSectorId(selectedSectorConfiguration).toString() === selectedSector) ||
            isNonActiveUserJamming
          }
          loading={isLoading}
          size={{ base: 'sm', '4k': 'md' }}
          onClick={handleSubmit}
        >
          <FormattedMessage id="global.validate" />
        </Button>
      </VStack>
    </PopupContainer>
  );
}
